import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";

import {
  stripeElementsModes,
  type StripeElementsMode,
} from "@/react/components/Paywalls/StripeElements/types";
import { isDigitalWallet } from "@/react/helpers/paywallCheckoutHelpers";

import { CheckoutError } from "../../CheckoutError";
import { generateIdempotencyKey } from "../../helpers/generateIdempotencyKey";
import { useGetStripeElements } from "../../useStripeElements";
import type { MemberAddressState, OnStageChange } from "../types";
import { stages } from "../types";
import {
  submitSetupIntent,
  submitPaymentIntent,
  PaymentIntentSubmitError,
} from "./submitAttemptHelpers";
import { useConfirmPayment } from "./useConfirmPayment";
import { useCreateAttempt } from "./useCreateAttempt";
import { useRecoverPayment } from "./useRecoverPayment";

interface UseSubmitAttemptParams {
  canSubmitAttempt: () => boolean;
  checkoutIntentToken?: string;
  communityId: number;
  communityProcessorPaymentMethodConfigurationId: string;
  doesCommunityProcessorSupportsLink: boolean;
  doesMemberHasDefaultPaymentMethod: boolean;
  externalTransactionToken?: string;
  isExpressMode: boolean;
  isMemberUsingDefaultPaymentMethod: boolean;
  isOneTimePrice: boolean;
  isPaymentMethodRequired: boolean;
  memberAddress: MemberAddressState;
  memberProcessorCustomerSessionClientSecret?: string;
  onStageChange: OnStageChange;
  paymentMethodType: string;
  paywallPriceId: number;
  sessionId?: number;
  sessionStepId?: number;
  stripeElementsMode: StripeElementsMode;
}

export const useSubmitAttempt = ({
  canSubmitAttempt,
  checkoutIntentToken,
  communityId,
  communityProcessorPaymentMethodConfigurationId,
  doesCommunityProcessorSupportsLink,
  doesMemberHasDefaultPaymentMethod,
  externalTransactionToken,
  isExpressMode,
  isMemberUsingDefaultPaymentMethod,
  isOneTimePrice,
  isPaymentMethodRequired,
  memberAddress,
  stripeElementsMode,
  memberProcessorCustomerSessionClientSecret,
  onStageChange,
  paymentMethodType,
  paywallPriceId,
  sessionId,
  sessionStepId,
}: UseSubmitAttemptParams) => {
  const navigate = useNavigate();
  const getStripeElements = useGetStripeElements();

  const idempotencyKeyRef = useRef<string | undefined>(undefined);

  const replaceUrl = useCallback(
    (url: string) => {
      void navigate({ to: url, replace: true });
    },
    [navigate],
  );

  // setup mode = zero amount; see getStripeElementsMode for the mode matrix.
  const hasToSetupPaymentMethod = useMemo(
    () =>
      stripeElementsMode === stripeElementsModes.setup &&
      isPaymentMethodRequired,
    [stripeElementsMode, isPaymentMethodRequired],
  );

  // MUST be called before async work — digital wallets fail if delayed.
  const submitElements = useCallback(
    async (caller: string) => {
      const { stripeElements } = getStripeElements(caller);
      const result = await stripeElements.submit();
      if (result?.error) {
        throw CheckoutError.fromStripeError(caller, result.error);
      }
    },
    [getStripeElements],
  );

  const createAttempt = useCreateAttempt({
    checkoutIntentToken,
    communityId,
    externalTransactionToken,
    paymentMethodType,
    paywallPriceId,
    sessionStepId,
  });
  const confirmPayment = useConfirmPayment({
    communityProcessorPaymentMethodConfigurationId,
    doesCommunityProcessorSupportsLink,
    doesMemberHasDefaultPaymentMethod,
    isExpressMode,
    isMemberUsingDefaultPaymentMethod,
    isOneTimePrice,
    memberProcessorCustomerSessionClientSecret,
  });
  const resubmitElementsBeforeRetry = useMemo(() => {
    if (isExpressMode || isDigitalWallet(paymentMethodType)) {
      return undefined;
    }

    return () => submitElements("useRecoverPayment.retry.submitElements");
  }, [isExpressMode, paymentMethodType, submitElements]);
  const recoverPayment = useRecoverPayment({
    confirmPayment,
    paymentMethodType,
    resubmitElementsBeforeRetry,
  });

  const submitAttempt = useCallback(
    async (data: Record<string, unknown>) => {
      if (!canSubmitAttempt()) {
        const error = CheckoutError.generic(
          "useSubmitAttempt.submitAttempt.cannotSubmit",
        );
        onStageChange({ stage: stages.failed, error });
        return;
      }

      // Generated once; retries reuse the same key so the server deduplicates them.
      idempotencyKeyRef.current ??= generateIdempotencyKey();

      // In non-express paid checkout, PaymentElement must have produced a paymentMethodType
      // before submission; otherwise Stripe can receive [""] and unmount the element.
      // Express mode and free/trial checkout legitimately submit without one.
      if (!isExpressMode && isPaymentMethodRequired && !paymentMethodType) {
        idempotencyKeyRef.current = undefined;
        const error = CheckoutError.generic(
          "useSubmitAttempt.submitAttempt.paymentMethodNotLoaded",
        );
        onStageChange({ stage: stages.failed, error });
        return;
      }

      // Merge form data into memberAddress for billing_details (new users fill form name/email).
      const billingMemberAddress: MemberAddressState = {
        ...memberAddress,
        name: (data.name as string | undefined) || memberAddress.name,
        email: (data.email as string | undefined) || memberAddress.email,
      };

      // Merge memberAddress into billing_info_attributes, preserving tax_id/business_name from form data.
      const existingBillingInfo =
        (data.community_member_billing_info_attributes as
          | Record<string, unknown>
          | undefined) ?? {};
      const billingInfoAttributes = {
        ...existingBillingInfo,
        address_city: memberAddress.city,
        address_country: memberAddress.country,
        address_line1: memberAddress.line1,
        address_line2: memberAddress.line2,
        address_postal_code: memberAddress.postalCode,
        address_state: memberAddress.state,
      };
      const submissionData = {
        ...data,
        idempotency_key: idempotencyKeyRef.current,
      };

      try {
        // Submit elements FIRST, before any async work. This is critical for digital
        // wallets (Apple Pay, Google Pay) which have a short time window for submission.
        // Skip in Express Mode - no PaymentElement rendered, we use saved payment method directly.
        if (!isExpressMode) {
          await submitElements("useSubmitAttempt.submitAttempt");
        }

        if (hasToSetupPaymentMethod) {
          await submitSetupIntent({
            billingInfoAttributes,
            billingMemberAddress,
            checkoutIntentToken,
            communityId,
            createAttempt,
            data: submissionData,
            getStripeElements,
            isExpressMode,
            onStageChange,
            paymentMethodType,
            paywallPriceId,
            replaceUrl,
            sessionId,
          });
          idempotencyKeyRef.current = undefined;
          return;
        }

        await submitPaymentIntent({
          billingInfoAttributes,
          billingMemberAddress,
          confirmPayment,
          createAttempt,
          data: submissionData,
          isExpressMode,
          onStageChange,
          paymentMethodType,
          replaceUrl,
          sessionId,
        });
        idempotencyKeyRef.current = undefined;
      } catch (e: unknown) {
        // submitPaymentIntent wraps confirmPayment failures in PaymentIntentSubmitError,
        // which carries the partial PI state needed for recovery (attempt, returnUrl,
        // navigateToVerification). This avoids mutable closures as recovery context.
        if (e instanceof PaymentIntentSubmitError) {
          const { attempt, returnUrl, navigateToVerification, checkoutError } =
            e;
          const result = await recoverPayment(
            checkoutError,
            attempt,
            billingMemberAddress,
            returnUrl,
          );
          if (result.shouldNavigate) {
            idempotencyKeyRef.current = undefined;
            navigateToVerification();
            return;
          }
          idempotencyKeyRef.current = undefined;
          onStageChange({ stage: stages.failed, error: result.error });
          return;
        }
        const error = CheckoutError.fromUnknownError("useSubmitAttempt", e);
        if (e instanceof CheckoutError) {
          idempotencyKeyRef.current = undefined;
        }
        onStageChange({ stage: stages.failed, error });
      }
    },
    [
      canSubmitAttempt,
      communityId,
      confirmPayment,
      createAttempt,
      getStripeElements,
      hasToSetupPaymentMethod,
      recoverPayment,
      isExpressMode,
      isPaymentMethodRequired,
      memberAddress,
      onStageChange,
      paymentMethodType,
      paywallPriceId,
      replaceUrl,
      sessionId,
      submitElements,
      checkoutIntentToken,
      externalTransactionToken,
    ],
  );

  return submitAttempt;
};
